import { ArrowLeftIcon, EyeIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { InputType } from 'config'
import { useEffect, useState } from 'react'
import { getAccountingAccount, getAccountingReconciliationDetail, getAccountingReconciliationHistory } from 'services'
import { svgLoading } from 'stories/assets'
import { Modal } from 'stories/components'
import { formatDate, thousandSeperator } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { AccountTransactionTypeOptions } from '../AccountTransactions/types'

interface ITransaction {
  id: number
  date: string
  refNo: string
  type: string
  payee: string
  memo: string
  payment: number
  deposit: number
}

interface IReconciliation {
  id: number
  accountNo: number
  statementDate: string
  beginningBalance: number
  endingBalance: number
  serviceCharge: number
  interest: number
  clearedBalance: number
  reconciledTransactionIds: number[]
  reconciledBy: string
  createdAt: string
}

export const ReconcileHistory = ({ onBack }: { onBack?: () => void }) => {
  const [isLoading, setLoading] = useState(false)
  const [accounts, setAccounts] = useState<any[]>([])
  const [selectedAccountNo, setSelectedAccountNo] = useState<string>('')
  const [history, setHistory] = useState<IReconciliation[]>([])
  const [selectedItem, setSelectedItem] = useState<IReconciliation | null>(null)
  const [detailTransactions, setDetailTransactions] = useState<ITransaction[]>([])
  const [detailLoading, setDetailLoading] = useState(false)

  useEffect(() => {
    getAccountingAccount().then((data) => {
      setAccounts(data)
    })
  }, [])

  useEffect(() => {
    if (selectedAccountNo) {
      fetchHistory(Number(selectedAccountNo))
    } else {
      setHistory([])
    }
  }, [selectedAccountNo])

  const fetchHistory = async (accountNo: number) => {
    setLoading(true)
    try {
      const data = await getAccountingReconciliationHistory(accountNo)
      setHistory(data || [])
    } catch (e) {
      console.error('Error fetching reconciliation history:', e)
      setHistory([])
    }
    setLoading(false)
  }

  const accountOptions: Record<string, string> = {}
  accounts.forEach((acc) => {
    accountOptions[String(acc.no)] = `${acc.name} (${acc.number || acc.no})`
  })

  const accountInput: InputType = {
    inputType: 'select',
    title: 'Select Account',
    options: accountOptions,
    value: selectedAccountNo,
    hasDefaultOption: true,
  }

  const onViewDetail = async (item: IReconciliation) => {
    setSelectedItem(item)
    setDetailTransactions([])
    setDetailLoading(true)
    try {
      const detail = await getAccountingReconciliationDetail(item.accountNo, item.id)
      setDetailTransactions(detail.transactions || [])
    } catch (e) {
      console.error('Error fetching reconciliation detail:', e)
    }
    setDetailLoading(false)
  }

  const getAccountName = (accountNo: number) => {
    const acc = accounts.find((a) => a.no === accountNo)
    return acc ? `${acc.name} (${acc.number || acc.no})` : `Account #${accountNo}`
  }

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-3 items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center pb-5">
          {onBack && (
            <span
              className="mr-3 cursor-pointer text-shade-blue hover:text-blue-700"
              onClick={onBack}
              title="Back to Chart of Accounts"
            >
              <ArrowLeftIcon className="w-6 h-6" />
            </span>
          )}
          Reconcile History
          {isLoading && (
            <span className="ml-3">
              <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
            </span>
          )}
        </h1>
      </div>

      <div className="mb-6 max-w-md">
        <RenderInput
          input={accountInput}
          Key="accountSelect"
          onChange={(_key: string, value: string) => setSelectedAccountNo(value)}
        />
      </div>

      <div className="relative overflow-auto">
        <LayoutLoading show={isLoading} />

        {selectedAccountNo && !isLoading && history.length === 0 && (
          <div className="text-gray-500 text-center py-8">No reconciliation history found for this account.</div>
        )}

        {history.length > 0 && (
          <div className="sm:rounded-lg mb-8">
            <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
              <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th scope="col" className="px-3 py-3 w-[20px]">
                    No
                  </th>
                  <th scope="col" className="px-3 py-3">
                    Statement Date
                  </th>
                  <th scope="col" className="px-3 py-3">
                    Reconciled Date
                  </th>
                  <th scope="col" className="px-3 py-3 text-right">
                    Beginning Balance
                  </th>
                  <th scope="col" className="px-3 py-3 text-right">
                    Ending Balance
                  </th>
                  <th scope="col" className="px-3 py-3 text-right">
                    Cleared Balance
                  </th>
                  <th scope="col" className="px-3 py-3 text-center">
                    Transactions
                  </th>
                  <th scope="col" className="px-3 py-3">
                    Reconciled By
                  </th>
                  <th scope="col" className="px-3 py-3">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {history.map((item, index) => {
                  return (
                    <tr className="bg-white border-b dark:bg-gray-800 dark:border-gray-700" key={item.id}>
                      <td className="px-3 py-2 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                        {index + 1}
                      </td>
                      <td className="px-3 py-2 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                        {formatDate(item.statementDate)}
                      </td>
                      <td className="px-3 py-2 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                        {formatDate(item.createdAt)}
                      </td>
                      <td className="px-3 py-2 font-medium text-gray-900 dark:text-white whitespace-nowrap text-right">
                        ${thousandSeperator(item.beginningBalance)}
                      </td>
                      <td className="px-3 py-2 font-medium text-gray-900 dark:text-white whitespace-nowrap text-right">
                        ${thousandSeperator(item.endingBalance)}
                      </td>
                      <td className="px-3 py-2 font-medium text-gray-900 dark:text-white whitespace-nowrap text-right">
                        ${thousandSeperator(item.clearedBalance)}
                      </td>
                      <td className="px-3 py-2 font-medium text-gray-900 dark:text-white whitespace-nowrap text-center">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {item.reconciledTransactionIds?.length || 0}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                        {item.reconciledBy}
                      </td>
                      <td className="px-3 py-2">
                        <button
                          className="text-shade-blue p-1 hover-shadow1 cursor-pointer rounded"
                          onClick={() => onViewDetail(item)}
                          title="View Details"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedItem && (
        <Modal
          isOpen={!!selectedItem}
          title={`Reconciliation Details - ${formatDate(selectedItem.statementDate)}`}
          onClose={() => {
            setSelectedItem(null)
            setDetailTransactions([])
          }}
          titleOkay=""
          titleCancel="Close"
        >
          <div className="w-[900px]">
            <div className="bg-gray-50 p-4 rounded mb-4">
              <h3 className="font-semibold mb-3">Summary</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Account:</span>
                  <span className="font-medium">{getAccountName(selectedItem.accountNo)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Statement Date:</span>
                  <span className="font-medium">{formatDate(selectedItem.statementDate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Reconciled Date:</span>
                  <span className="font-medium">{formatDate(selectedItem.createdAt)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Reconciled By:</span>
                  <span className="font-medium">{selectedItem.reconciledBy}</span>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded mb-4">
              <h3 className="font-semibold mb-3">Balances</h3>
              <div className="text-sm space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Beginning Balance:</span>
                  <span className="font-medium">${thousandSeperator(selectedItem.beginningBalance)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Ending Balance:</span>
                  <span className="font-medium">${thousandSeperator(selectedItem.endingBalance)}</span>
                </div>
                {Number(selectedItem.serviceCharge) > 0 && (
                  <div className="flex justify-between text-red-600">
                    <span>Service Charge:</span>
                    <span>-${thousandSeperator(selectedItem.serviceCharge)}</span>
                  </div>
                )}
                {Number(selectedItem.interest) > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Interest Earned:</span>
                    <span>+${thousandSeperator(selectedItem.interest)}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold border-t pt-2">
                  <span>Cleared Balance:</span>
                  <span>${thousandSeperator(selectedItem.clearedBalance)}</span>
                </div>
                <div
                  className={`flex justify-between font-bold ${
                    Math.abs(Number(selectedItem.clearedBalance) - Number(selectedItem.endingBalance)) < 0.01
                      ? 'text-green-600'
                      : 'text-red-600'
                  }`}
                >
                  <span>Difference:</span>
                  <span>
                    $
                    {thousandSeperator(
                      Math.round((Number(selectedItem.clearedBalance) - Number(selectedItem.endingBalance)) * 100) /
                        100,
                    )}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded">
              <h3 className="font-semibold mb-3">
                Reconciled Transactions ({selectedItem.reconciledTransactionIds?.length || 0})
              </h3>
              {detailLoading ? (
                <div className="flex justify-center py-4">
                  <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
                </div>
              ) : detailTransactions.length > 0 ? (
                <div className="max-h-80 overflow-auto border rounded">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left">Date</th>
                        <th className="px-3 py-2 text-left">Type</th>
                        <th className="px-3 py-2 text-left">Ref No</th>
                        <th className="px-3 py-2 text-left">Payee</th>
                        <th className="px-3 py-2 text-right">Payment</th>
                        <th className="px-3 py-2 text-right">Deposit</th>
                        <th className="px-3 py-2 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailTransactions.map((txn) => {
                        const amount = Number(txn.deposit || 0) - Number(txn.payment || 0)
                        return (
                          <tr key={txn.id} className="border-b hover:bg-gray-50">
                            <td className="px-3 py-1.5">{formatDate(txn.date)}</td>
                            <td className="px-3 py-1.5">
                              {AccountTransactionTypeOptions[txn.type as keyof typeof AccountTransactionTypeOptions] ||
                                txn.type}
                            </td>
                            <td className="px-3 py-1.5">{txn.refNo}</td>
                            <td className="px-3 py-1.5">{txn.payee}</td>
                            <td className="px-3 py-1.5 text-right">
                              {Number(txn.payment) ? `$${thousandSeperator(txn.payment)}` : ''}
                            </td>
                            <td className="px-3 py-1.5 text-right">
                              {Number(txn.deposit) ? `$${thousandSeperator(txn.deposit)}` : ''}
                            </td>
                            <td className={`px-3 py-1.5 text-right font-medium ${amount < 0 ? 'text-red-600' : ''}`}>
                              ${thousandSeperator(Math.abs(amount))}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                    <tfoot className="bg-gray-100 font-semibold">
                      <tr>
                        <td colSpan={4} className="px-3 py-2 text-right">
                          Totals:
                        </td>
                        <td className="px-3 py-2 text-right">
                          ${thousandSeperator(detailTransactions.reduce((s, t) => s + Number(t.payment || 0), 0))}
                        </td>
                        <td className="px-3 py-2 text-right">
                          ${thousandSeperator(detailTransactions.reduce((s, t) => s + Number(t.deposit || 0), 0))}
                        </td>
                        <td className="px-3 py-2 text-right">
                          $
                          {thousandSeperator(
                            Math.abs(
                              detailTransactions.reduce(
                                (s, t) => s + Number(t.deposit || 0) - Number(t.payment || 0),
                                0,
                              ),
                            ),
                          )}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-gray-500">No transaction data available.</p>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
